import React, { useMemo, useState } from 'react';

interface ReportItem {
  id: string;
  type: 'product' | 'seller';
  targetId?: string;
  name: string;
  email: string;
  reason: string;
  createdAt: string;
}

function saveLocal(report: ReportItem) {
  try {
    const key = 'reports';
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.unshift(report);
    localStorage.setItem(key, JSON.stringify(existing));
  } catch {}
}

async function submitReport(item: Omit<ReportItem, 'id' | 'createdAt'>) {
  const payload: ReportItem = {
    ...item,
    id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
    createdAt: new Date().toISOString(),
  };
  try {
    const res = await fetch('/api/reports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Server error');
  } catch {
    saveLocal(payload);
  }
  return payload;
}

const ReportPage: React.FC = () => {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const prefillType = (url?.searchParams.get('type') as 'product' | 'seller') || 'product';
  const prefillTargetId = url?.searchParams.get('id') || '';
  const prefillTitle = url?.searchParams.get('title') || '';
  const prefillSeller = url?.searchParams.get('seller') || '';

  const [type, setType] = useState<'product' | 'seller'>(prefillType);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [targetId, setTargetId] = useState(prefillTargetId);
  const [reason, setReason] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const contextSummary = useMemo(() => {
    const parts = [] as string[];
    if (prefillTitle) parts.push(`Product: ${prefillTitle}`);
    if (prefillSeller) parts.push(`Seller: ${prefillSeller}`);
    if (prefillTargetId) parts.push(`ID: ${prefillTargetId}`);
    return parts.join(' • ');
  }, [prefillTitle, prefillSeller, prefillTargetId]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !reason) return;
    setLoading(true);
    await submitReport({ type, targetId, name, email, reason });
    setLoading(false);
    setShowModal(true);
    setName('');
    setEmail('');
    setReason('');
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-3">Report {type === 'product' ? 'Product' : 'Seller'}</h1>
      {contextSummary && (
        <div className="text-xs md:text-sm text-gray-600 mb-4">{contextSummary}</div>
      )}

      <form onSubmit={onSubmit} className="bg-white rounded-lg shadow-sm p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div>
            <label className="block text-xs md:text-sm text-gray-700 mb-1">Report Type</label>
            <select value={type} onChange={(e) => setType(e.target.value as 'product' | 'seller')} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="product">Product</option>
              <option value="seller">Seller</option>
            </select>
          </div>
          <div>
            <label className="block text-xs md:text-sm text-gray-700 mb-1">Target ID (optional)</label>
            <input value={targetId} onChange={(e) => setTargetId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ID to help us locate the item" />
          </div>
          <div>
            <label className="block text-xs md:text-sm text-gray-700 mb-1">Your Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Your name" />
          </div>
          <div>
            <label className="block text-xs md:text-sm text-gray-700 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="you@example.com" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs md:text-sm text-gray-700 mb-1">Reason</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={5} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Explain the issue"></textarea>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button type="submit" disabled={loading} className="inline-flex items-center rounded-md bg-red-600 text-white px-4 py-2 text-sm md:text-base hover:bg-red-700 disabled:opacity-60">
            {loading ? 'Sending…' : 'Submit Report'}
          </button>
        </div>
      </form>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg p-4 md:p-6 w-[90%] max-w-md shadow-lg">
            <div className="flex items-center gap-2 text-green-700 mb-2">
              <i className="ri-checkbox-circle-line text-xl"></i>
              <span className="font-medium">Report submitted</span>
            </div>
            <p className="text-sm md:text-base text-gray-700">We have taken your concern into consideration and we will contact you soon.</p>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setShowModal(false)} className="rounded-md bg-blue-600 text-white px-4 py-2 text-sm md:text-base hover:bg-blue-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPage;